import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, mergeMap } from 'rxjs';
import { JsonConvert } from 'json2typescript';

import { environment } from '../../../environment/environment';
import { keycloakLogout } from '../../../auth/keycloak.config';
import { Paging } from '../../../model/paging/paging';
import { FloorPlan, FloorPlanCreateResponse, FloorPlanData, FloorPlanDataArray } from '../../../model/floorplan/floorplan';
import { HttpUtilsService } from '../../commons/http-uilis.service';

@Injectable({
  providedIn: 'root'
})
export class FloorPlanService {
  private _url: string = `${environment.baseAPIURL}`;

  constructor(private _http: HttpClient, private _httpUtils: HttpUtilsService) {}

  GETPaginatedFloorPlans(projectId: string, paging: Paging): Observable<FloorPlan> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.get(`${this._url}/floorplans${this.buildQueryString(projectId, paging)}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              const response: FloorPlanDataArray = jsonConvert.deserializeObject(res, FloorPlanDataArray);
              if (response.success) {
                return response;
              } else {
                this._httpUtils.handleError(response);
                return;
              }
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  GETFloorPlanById(floorPlanId: string): Observable<FloorPlan> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.get(`${this._url}/floorplans/${floorPlanId}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              const response: FloorPlanData = jsonConvert.deserializeObject(res, FloorPlanData);
              if (response.success) {
                return response;
              } else {
                this._httpUtils.handleError(response);
                return;
              }
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  POSTFloorPlan(body: FloorPlan) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.post(`${this._url}/floorplans`, this.toCreatePayload(body), { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              return jsonConvert.deserializeObject(res, FloorPlanCreateResponse);
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  PUTFloorPlanScale(floorPlanId: string, unitsPerMeter: number | null) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.put(`${this._url}/floorplans/${floorPlanId}/scale`, { unitsPerMeter }, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  DELETEFloorPlan(floorPlanId: string) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.delete(`${this._url}/floorplans/${floorPlanId}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  private buildQueryString(projectId: string, paging: Paging): string {
    const params = new URLSearchParams({
      projectId,
      offset: String((paging.pageIndex - 1) * paging.pageSize),
      limit: String(paging.pageSize)
    });
    return `?${params.toString()}`;
  }

  private toCreatePayload(body: FloorPlan) {
    return {
      projectId: body.projectId,
      name: body.name,
      svgContent: body.svgContent,
      unitsPerMeter: body.unitsPerMeter
    };
  }
}
